import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import {
    AppState,
    AuthService,
    BlockSessionModalComponent,
    CacheService,
    CardsService,
    CONFIG,
    ConfigService,
    PageService,
    PageType,
    PillarType,
    SettingsService,
    TranslateService,
} from '@gallaghercp/core-frontend';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of, switchMap } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ClientService implements CanActivate, CanActivateChild {
    protected auth = inject(AuthService);
    protected router = inject(Router);
    protected pages = inject(PageService);
    protected cache = inject(CacheService);
    protected settings = inject(SettingsService);
    protected modalService = inject(BsModalService);
    protected translate = inject(TranslateService);
    protected config = inject(ConfigService);

    DEFAULT = {
        name: 'Core',
        login: '',
        welcome: '',
        support: 'Need help signing in?',
        uid: 'Employee ID',
        uidHelper: false,
        dobFormat: 'DD/MM/YYYY',
    };

    provider: any;

    private sessionTimeoutModal?: BsModalRef;

    constructor() {
        this.auth.sessionEmit.subscribe(ping => {
            if (!this.sessionTimeoutModal) {
                this.showSessionTimeoutModal(ping);
            }
        });
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        return this.canActivate(childRoute, state);
    }

    canActivate(
        _next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        this.auth.setRedirectUrl(state.url);
        return this.auth.validateAuthentication().pipe(
            take(1),
            switchMap(() => {
                if (this.auth.isAuthenticated()) {
                    return of(true);
                }
                return this.getAccessType().pipe(
                    map((type: any) => {
                        if ((type === 'public' || type === 'mixed') && state.url !== '/profile') {
                            return true;
                        }
                        this.router.navigate(['/login']);
                        return false;
                    }),
                );
            }),
        );
    }

    getConfiguration() {
        this.provider = this.auth.serviceProvider;
        return this.provider && this.provider.configuration ? this.provider.configuration : {};
    }

    getAccessType(): Observable<AppState> {
        return this.settings.getAuthenticationType().pipe(
            map((authenticationType: any) => {
                if (authenticationType !== undefined) {
                    return authenticationType;
                }
                return this.getConfiguration().accessType || 'individual';
            }),
        );
    }

    getLogoURL() {
        return this.settings.getLogo().pipe(
            map((logoUrl: any) => {
                if (this.getConfiguration().logo) {
                    return this.getConfiguration().logo;
                }
                if (logoUrl !== undefined) {
                    return logoUrl;
                }
                return 'https://storage.googleapis.com/archipelago-assets/Gallagher_HorizontalLarge-3D.png';
            }),
        );
    }

    getSiteName() {
        return this.config.get('appName') || this.getConfiguration().name || this.DEFAULT.name;
    }

    getLoginTitle() {
        return this.getConfiguration().auth && this.getConfiguration().auth.login
            ? this.getConfiguration().auth.login.title
            : this.DEFAULT.login;
    }

    getLoginWelcomeText() {
        return this.getConfiguration().auth && this.getConfiguration().auth.login
            ? this.getConfiguration().auth.login.welcomeText
            : this.DEFAULT.welcome;
    }

    getUID(): string {
        return (this.config.get('auth') as { UID_LABEL?: string })?.UID_LABEL ?? this.DEFAULT.uid;
    }

    getUIDMask(): string {
        return (this.config.get('auth') as { UID_MASK?: string })?.UID_MASK ?? '';
    }

    getUIDPlaceholder(): string {
        return (this.config.get('auth') as { UID_PLACEHOLDER?: string })?.UID_PLACEHOLDER ?? '';
    }

    getUserNotifications(clear?: boolean) {
        return clear
            ? this.cache.getListThroughCache(
                  'user-notifications',
                  { interacted: false },
                  {},
                  Date.now() as unknown as string,
              )
            : this.cache.getListThroughCache('user-notifications', {
                  interacted: false,
              });
    }

    showSessionTimeoutModal(ping: number): void {
        this.sessionTimeoutModal = this.modalService.show(BlockSessionModalComponent, {
            backdrop: 'static',
            keyboard: false,
            class: 'modal-md modal--session text-center modal-dialog-centered',
            animated: true,
            initialState: {
                ping,
            },
        });

        if (this.sessionTimeoutModal) {
            this.sessionTimeoutModal.onHidden?.subscribe((): void => {
                this.sessionTimeoutModal = undefined;
            });
        }
    }

    hideSessionModals(): void {
        this.sessionTimeoutModal?.hide();
    }

    getPageCategories() {
        return this.cache.getListThroughCache('page-categories').pipe(
            map((categories: any[any]) => {
                const allPages: any[] = [];
                categories.forEach((category: { pageCategories: [] }) => {
                    if (category.pageCategories) {
                        category.pageCategories.forEach((page: { pageId: number }) => {
                            if (allPages.filter((e: { id: number }) => e.id === page.pageId).length === 0) {
                                allPages.push(page);
                            }
                        });
                    }
                });
                if (categories.filter((e: { name: string }) => e.name === 'All').length === 0) {
                    categories.unshift({
                        id: 0,
                        name: 'All',
                        pageCategories: allPages,
                    });
                }
                return categories;
            }),
        );
    }

    getPillars() {
        return this.cache.getListThroughCache('page-categories').pipe(
            map((categories: any[any]) => {
                const allPages: any[] = [];
                const pillars = categories.filter((pillar: PillarType) => {
                    return CardsService.PILLARS.includes(pillar.name);
                });
                pillars.forEach((category: { pageCategories: [] }) => {
                    if (category.pageCategories) {
                        category.pageCategories.forEach((page: { url: string }) => {
                            if (allPages.filter((e: { url: string }) => e.url === page.url).length === 0) {
                                allPages.push(page);
                            }
                        });
                    }
                });

                pillars.forEach((category: { pageCategories: [] }) => {
                    (category.pageCategories ?? []).forEach((page: PageType) => {
                        page.name = this.translate.getPageSpecific(page, 'name');
                        page.description = this.translate.getPageSpecific(page, 'description');
                        page.url = this.translate.getPageSpecific(page, 'url');
                        page.tags = this.translate.getPageSpecific(page, 'tags');
                        if (allPages.filter((e: PageType) => e.url === page.url).length === 0) {
                            allPages.push(page);
                        }
                    });
                });

                // if (pillars.filter((e: { name: string; }) => e.name === 'All').length === 0) {
                //     pillars.push({id: 0, name: 'All', pageCategories: allPages})
                // }

                pillars.forEach((pillar: any) => {
                    pillar.pillarClass = pillar.name
                        .replace(new RegExp(' ', 'g'), '-')
                        .replace(new RegExp('/', 'g'), '-')
                        .toLowerCase();
                });

                return pillars;
            }),
        );
    }
}
